/**
 * useAssessment Hook
 * React Query hook for fetching a single assessment
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useTenant } from "@/contexts/TenantContext";
import type { Assessment } from "./useAssessments";
import { AssessmentWorkflowState } from "@app/types";

/**
 * React Query key for a single assessment. Document mutations invalidate it so
 * `trackStatuses` (document count, processingDocumentCount) stays current for
 * the Start Analysis gate (sv-zlxt.4).
 *
 * @param tenantId - The tenant the assessment belongs to.
 * @param assessmentId - The assessment.
 * @returns The query key.
 */
export function assessmentQueryKey(
  tenantId: string | undefined,
  assessmentId: string,
): readonly ["assessment", string | undefined, string] {
  return ["assessment", tenantId, assessmentId] as const;
}

/** How often to re-fetch the assessment while its documents are processing (sv-zlxt.4). */
export const PROCESSING_POLL_INTERVAL_MS = 5000;

/**
 * Poll the assessment while any Document is still pending/processing, so the
 * Start Analysis gate (which waits for them) re-opens without a page reload.
 *
 * @param assessment - The cached assessment, or undefined before the first fetch.
 * @returns The poll interval in ms, or `false` to stop polling.
 */
export function assessmentRefetchInterval(
  assessment: Assessment | undefined,
): number | false {
  return (assessment?.trackStatuses?.processingDocumentCount ?? 0) > 0
    ? PROCESSING_POLL_INTERVAL_MS
    : false;
}

export interface UseAssessmentOptions {
  tenantIdOverride?: string;
}

export interface UseAssessmentResult {
  assessment: Assessment | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  updateWorkflowState: (newState: AssessmentWorkflowState) => void;
  isUpdatingWorkflowState: boolean;
  updateTitle: (newTitle: string) => Promise<Assessment>;
  isUpdatingTitle: boolean;
}

export function useAssessment(
  assessmentId: string,
  options?: UseAssessmentOptions,
): UseAssessmentResult {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();

  const tenantId = options?.tenantIdOverride ?? currentTenant?.id;
  const isReadOnly = !!options?.tenantIdOverride;

  const { data, isLoading, error, refetch } = useQuery<Assessment, Error>({
    queryKey: assessmentQueryKey(tenantId, assessmentId),
    queryFn: async () => {
      if (!tenantId) {
        throw new Error("No tenant selected");
      }

      if (!assessmentId) {
        throw new Error("Assessment ID is required");
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url = `/tenants/${tenantId}/assessments/${assessmentId}` as any;
      const { data, error } = await apiClient.GET(url);

      if (error || !data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to fetch assessment",
        );
      }

      return data as Assessment;
    },
    enabled: !!tenantId && !!assessmentId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: (query) => assessmentRefetchInterval(query.state.data),
    refetchIntervalInBackground: false,
  });

  const updateWorkflowStateMutation = useMutation({
    mutationFn: async (newState: AssessmentWorkflowState) => {
      if (!tenantId) throw new Error("No tenant selected");
      if (isReadOnly)
        throw new Error("Cannot modify assessment in read-only mode");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url = `/tenants/${tenantId}/assessments/${assessmentId}` as any;
      const { data, error } = await apiClient.PATCH(url, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { workflowState: newState } as any,
      });

      if (error || !data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to update workflow state",
        );
      }

      return data as Assessment;
    },
    onSuccess: (updatedAssessment) => {
      // Update the cached assessment data
      queryClient.setQueryData(
        ["assessment", tenantId, assessmentId],
        updatedAssessment,
      );
      // Also refetch to ensure we have the latest data
      refetch();
    },
  });

  const updateTitleMutation = useMutation({
    mutationFn: async (newTitle: string) => {
      if (!tenantId) throw new Error("No tenant selected");
      if (isReadOnly)
        throw new Error("Cannot modify assessment in read-only mode");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url = `/tenants/${tenantId}/assessments/${assessmentId}` as any;
      const { data, error } = await apiClient.PATCH(url, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { title: newTitle } as any,
      });

      if (error || !data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error((error as any)?.message || "Failed to update title");
      }

      return data as Assessment;
    },
    onMutate: async (newTitle: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: ["assessment", tenantId, assessmentId],
      });

      // Snapshot the previous value
      const previousAssessment = queryClient.getQueryData<Assessment>([
        "assessment",
        tenantId,
        assessmentId,
      ]);

      // Optimistically update the cache
      if (previousAssessment) {
        queryClient.setQueryData<Assessment>(
          ["assessment", tenantId, assessmentId],
          { ...previousAssessment, title: newTitle },
        );
      }

      return { previousAssessment };
    },
    onError: (err, newTitle, context) => {
      // Rollback to previous value on error
      if (context?.previousAssessment) {
        queryClient.setQueryData(
          ["assessment", tenantId, assessmentId],
          context.previousAssessment,
        );
      }
    },
    onSuccess: (updatedAssessment) => {
      // Merge the server response over the cache: the PATCH returns the raw
      // row without derived fields (trackStatuses, counts), and dropping
      // trackStatuses would stop the processing poll and re-open the Start
      // Analysis gate mid-processing (sv-zlxt.4).
      queryClient.setQueryData<Assessment>(
        assessmentQueryKey(tenantId, assessmentId),
        (previous) =>
          previous ? { ...previous, ...updatedAssessment } : updatedAssessment,
      );
    },
  });

  return {
    assessment: data,
    isLoading,
    error,
    refetch,
    updateWorkflowState: updateWorkflowStateMutation.mutate,
    isUpdatingWorkflowState: updateWorkflowStateMutation.isPending,
    updateTitle: updateTitleMutation.mutateAsync,
    isUpdatingTitle: updateTitleMutation.isPending,
  };
}

/**
 * Mutation hook for STA-276 "Use a different source" action on the CodeScan
 * track. Calls the tenant-scoped DELETE endpoint that clears `codeSourceType`
 * and any uploaded zips, returning the assessment to the chooser state.
 *
 * On success this hook invalidates the assessment, codeUploads, and
 * connectedRepos queries so the UI immediately reflects the cleared state
 * across every consumer of those caches — callers don't need to refetch
 * manually.
 *
 * @param assessmentId - Assessment to reset.
 * @returns `{ resetCodeSource, isResetting, resetError }` — standard
 *   TanStack-mutation-style handle. `resetCodeSource()` resolves on success.
 * @throws {Error & { status?: number }} On API failure. `status === 409` when
 *   an active scan or immutable workflow state blocks the reset; `status ===
 *   404` when the assessment isn't found in the current tenant; missing
 *   `status` when the tenant context is unset before the call.
 */
export interface UseResetCodeSourceResult {
  /** Trigger the reset; resolves on success, throws on API failure. */
  resetCodeSource: () => Promise<void>;
  /** True while the DELETE call is in flight. */
  isResetting: boolean;
  /** Last error from the mutation, or `null` if none. */
  resetError: (Error & { status?: number }) | null;
}

export function useResetCodeSource(
  assessmentId: string,
): UseResetCodeSourceResult {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();

  const {
    mutateAsync: resetCodeSource,
    isPending: isResetting,
    error: resetError,
  } = useMutation<void, Error & { status?: number }, void>({
    mutationFn: async () => {
      if (!currentTenant) throw new Error("No tenant selected");
      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/assessments/${assessmentId}/code-source` as any;
      const { error, response } = await apiClient.DELETE(url);
      if (error) {
        const status = response?.status;
        const message =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to reset code source";
        const err = Object.assign(new Error(message), { status });
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assessment", currentTenant?.id, assessmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["codeUploads", currentTenant?.id, assessmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["connectedRepos", currentTenant?.id, assessmentId],
      });
    },
  });

  return {
    resetCodeSource,
    isResetting,
    resetError,
  };
}
